// =====================================================================================
// INVRT Radar - web console - the upsell surfaces (Story PS6)
//
// Two surfaces, both driven by contract C6's GET /me/upsell, on the canonical mirrors
// (Chip, Button, Card, the States family) with zero bespoke restyling:
//
//   1. UpsellPanel - one module card, or the whole not-subscribed catalogue. The
//      HIGH-INTENT denial branch (HighIntentPage) renders it focused on the denied
//      module; the account section renders the full catalogue. The request action posts
//      /me/entitlement-requests and carries its own state matrix: idle, pending, the 201
//      requested chip, the 409 already-open copy, and an honest error with the action
//      offered again. An operator's later decision (accepted / declined) is NOT visible
//      here: C6 exposes no member read of entitlement_requests, and none is polled.
//   2. AccessUpgradesSection - the account area's "Access and upgrades" tab: the
//      subscribed modules (from useEntitlements) and, separately, the not-subscribed
//      catalogue with the same request action.
//
// The upsell read is marketing data, not an authority hint: hiding a nav item or denying
// a route stays the server's job (E07A untouched), and a request raised here grants
// nothing by itself.
// =====================================================================================

import { useMutation } from "@tanstack/react-query";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Chip } from "@/components/ui/chip";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useApiClient } from "@/hooks/useApi";
import { copyForToken, type ErrorCopy } from "@/lib/errorTokens";
import { isApiError } from "@/lib/api";
import { requestModuleAccess, useUpsellModules, type UpsellModule } from "@/lib/upsell";

// ---- the request action's state matrix -----------------------------------------------------

type RequestState = "idle" | "pending" | "requested" | "already-requested" | "failed";

const REQUESTED_COPY = "Your request is with the RADAR operators for review.";
const ALREADY_REQUESTED_COPY =
  "An open request for this module already exists for your organisation. An operator will review it.";

function useModuleAccessRequest() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (moduleCode: string) => requestModuleAccess(api, moduleCode),
  });
}

function requestState(
  request: ReturnType<typeof useModuleAccessRequest>,
): RequestState {
  if (request.isPending) return "pending";
  if (request.isSuccess) return "requested";
  if (request.isError) {
    return isApiError(request.error) && request.error.token === "entitlement_request_already_open"
      ? "already-requested"
      : "failed";
  }
  return "idle";
}

/** The one place a failed request becomes words: token copy plus the correlation id. */
function RequestError({ error }: { error: unknown }) {
  if (!isApiError(error)) return null;
  const copy: ErrorCopy = copyForToken(error.token);
  return (
    <div role="alert" className="flex flex-col gap-1">
      <p className="text-sm font-medium text-ink">{copy.title}</p>
      <p className="text-sm text-ink-muted">{copy.body}</p>
      {error.correlationId ? (
        <p className="text-xs text-ink-faint">
          Correlation id: <code className="font-mono text-ink-muted">{error.correlationId}</code>
        </p>
      ) : null}
    </div>
  );
}

// ---- one module card -----------------------------------------------------------------------

function UpsellModuleCard({ module }: { module: UpsellModule }) {
  const request = useModuleAccessRequest();
  const state = requestState(request);
  const settled = state === "requested" || state === "already-requested";

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>{module.name}</CardTitle>
          {state === "requested" ? (
            <Chip signal="buy" dot>
              Access requested
            </Chip>
          ) : null}
          {state === "already-requested" ? (
            <Chip signal="comp" dot>
              Request already open
            </Chip>
          ) : null}
        </div>
        <CardDescription>{module.marketing_blurb}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-end gap-3">
        {state === "failed" ? <RequestError error={request.error} /> : null}
        {settled ? (
          <p className="text-sm text-ink-muted">
            {state === "requested" ? REQUESTED_COPY : ALREADY_REQUESTED_COPY}
          </p>
        ) : (
          <div>
            {/* size="sm" lifted to the 44px target with min-h-11, the pagination mirror's
                own pairing (PRD section 5 binding; WCAG 2.5.5). */}
            <Button
              size="sm"
              className="min-h-11"
              loading={state === "pending"}
              onClick={() => request.mutate(module.module_code)}
            >
              Request access
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- the panel -----------------------------------------------------------------------------

export function UpsellPanel({ focusModuleCode }: { /** Show only this module's card; honest fallback copy when it is not offered. */ focusModuleCode?: string }) {
  const upsell = useUpsellModules();

  if (upsell.isLoading) {
    return <LoadingState label="Loading module details…" />;
  }
  if (upsell.isError) {
    return <ErrorState error={upsell.error} onRetry={() => void upsell.refetch()} />;
  }

  const modules = upsell.data?.modules ?? [];

  if (focusModuleCode !== undefined) {
    const focused = modules.find((module) => module.module_code === focusModuleCode) ?? null;
    if (focused === null) {
      return (
        <EmptyState
          tone="empty"
          title="Nothing to request yet"
          body="This module is not listed in the active catalogue right now, so a request cannot be raised here."
        />
      );
    }
    return <UpsellModuleCard module={focused} />;
  }

  if (modules.length === 0) {
    return (
      <EmptyState
        tone="empty"
        title="Everything is already available"
        body="Your organisation holds every module in RADAR's active catalogue."
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {modules.map((module) => (
        <UpsellModuleCard key={module.module_code} module={module} />
      ))}
    </div>
  );
}

// ---- the account area section --------------------------------------------------------------

export function AccessUpgradesSection() {
  const entitlements = useEntitlements();
  const entitlementsPending = entitlements.isLoading || entitlements.isFetching;
  const held = entitlements.data?.modules ?? [];

  return (
    <section aria-labelledby="access-upgrades-heading" className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h2 id="access-upgrades-heading" className="text-base font-semibold text-ink">
          Access and upgrades
        </h2>
        <p className="text-sm text-ink-muted">
          The modules your organisation holds, and the ones you can ask an operator for.
        </p>
      </div>

      <section aria-labelledby="access-held-heading" className="flex flex-col gap-3">
        <h3 id="access-held-heading" className="text-sm font-semibold text-ink">
          Your organisation holds
        </h3>
        {entitlementsPending ? (
          <LoadingState label="Loading your organisation's access…" />
        ) : entitlements.isError ? (
          <ErrorState error={entitlements.error} onRetry={() => void entitlements.refetch()} />
        ) : held.length === 0 ? (
          <EmptyState
            tone="first-run"
            compact
            title="No modules yet"
            body="Your organisation does not hold any RADAR modules yet. Anything you can raise a request for is listed below."
          />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {held.map((module) => (
              <li key={`${module.product_code}/${module.service_code}/${module.module_code}`}>
                <Chip signal="buy" dot>
                  {module.module_code}
                </Chip>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="access-add-heading" className="flex flex-col gap-3">
        <h3 id="access-add-heading" className="text-sm font-semibold text-ink">
          Available to add
        </h3>
        <UpsellPanel />
      </section>
    </section>
  );
}
